import { SecondScreenOperation } from "@/Common/Models/SecondScreen/Enum/SecondScreenOperation";
import { ISecondScreenServiceComparableData } from "../Models/ISecondScreenServiceComparableData";

class SecondScreenService<T extends ISecondScreenServiceComparableData<T>> {
    private debounceUpdateRequestTimeOut: number = null;
    private lastUpdateTimeStamp: number = 0;
    private lastUpdateRequest: T;
    private childWindow: Window;

    constructor(
        private url: string,
        private serviceName: {
            noteServiceName: string;
            encounterUpdateServiceName: string;
        },
        private onChildWindowClosed: () => void,
        private updateDataDelay: number = 30
    ) { }

    public openWindow(initialData: T, tryOpenInSecondScreen?: boolean): void {
        if (!this.isChildWindowOpen()) {
            if (tryOpenInSecondScreen) {
                if (this.hasSecondScreen()) {
                    this.openWindowInSecondScreen(initialData);
                    return;
                }
                else
                    this.openWindowInCurrentScreen();
            }
            else {
                this.childWindow = window.open(this.url);
            }
            this.setChildWindowInitialData(initialData);
        }
        else {
            this.updateModel(initialData);
            this.childWindow.focus();
        }
    }

    public debounceUpdateModel(newData: T): void {
        if (this.lastUpdateRequest.isEqual(newData) && !this.hasDebounceTimeExpired()) {
            if (this.debounceUpdateRequestTimeOut)
                return;

            this.debounceUpdateRequestTimeOut = window.setTimeout(() => {
                this.updateModel(this.lastUpdateRequest);
            }, this.updateDataDelay * 1000);
        }
        else {
            if (this.debounceUpdateRequestTimeOut) {
                clearTimeout(this.debounceUpdateRequestTimeOut);
                this.debounceUpdateRequestTimeOut = null;
            }
            this.updateModel(newData);
        }
    }

    public triggerSecondScreenUpdate<TModel>(operation: SecondScreenOperation, payload?: TModel): void {
        if (this.isChildWindowOpen()) {
            const event = new CustomEvent<{ operation: SecondScreenOperation, payload?: TModel }>(this.serviceName.encounterUpdateServiceName, { detail: { operation, payload } });
            this.childWindow.dispatchEvent(event);
        }
    }

    public updateModel(newData: T): void {
        if (this.debounceUpdateRequestTimeOut) {
            clearTimeout(this.debounceUpdateRequestTimeOut);
            this.debounceUpdateRequestTimeOut = null;
        }

        if (this.isChildWindowOpen()) {
            this.lastUpdateRequest = newData;
            this.lastUpdateTimeStamp = Date.now();
            const event = new CustomEvent<T>(this.serviceName.noteServiceName, { detail: newData });
            this.childWindow.dispatchEvent(event);
        }
        else {
            this.childWindow = null;
        }
    }

    public closeWindow(): void {
        if (this.childWindow && !this.childWindow?.closed)
            this.childWindow.close();

        this.childWindow = null;
        this.onChildWindowClosed();
    }

    public isChildWindowOpen(): boolean {
        return this.childWindow && !this.childWindow?.closed;
    }

    private hasSecondScreen(): boolean {
        return window.screen["isExtended"] || ((window.screen.width / window.screen.height) > 2);
    }

    private async openWindowInSecondScreen(initialData: T): Promise<void> {
        try {
            const screenDetails = await (window as any).getScreenDetails();
            const otherScreen = screenDetails.screens.find(s => s !== screenDetails.currentScreen);
            const newWindowScreen = otherScreen ?? screenDetails.currentScreen;
            this.childWindow = window.open(
                this.url,
                "_blank",
                `left=${newWindowScreen.availLeft},top=${newWindowScreen.availTop},width=${newWindowScreen.availWidth},height=${newWindowScreen.availHeight}`
            );
        }
        catch {
            this.openWindowInCurrentScreen();
        }

        this.setChildWindowInitialData(initialData);
    }

    private openWindowInCurrentScreen(): void {
        this.childWindow = window.open(this.url, "_blank", `width=${window.innerWidth}, height=${window.innerHeight}`);
    }

    private setChildWindowInitialData(initialData: T): void {
        this.lastUpdateRequest = initialData;
        this.lastUpdateTimeStamp = Date.now();
        this.childWindow["initialData"] = initialData;
        this.childWindow["noteServiceName"] = this.serviceName.noteServiceName;
        this.childWindow["encounterUpdateServiceName"] = this.serviceName.encounterUpdateServiceName;
    }

    private hasDebounceTimeExpired(): boolean {
        return Date.now() - this.lastUpdateTimeStamp > this.updateDataDelay * 1000;
    }
}

export { SecondScreenService };
